/**
 * SQL 문장의 첫 동사와 트랜잭션 제어·세션 설정 판정
 * (22-B회차 트랜잭션 제어, 23-A회차 세션 설정. 2026-09-08 사용자 확정 4·22).
 *
 * `START TRANSACTION`·`BEGIN`·`COMMIT`·`ROLLBACK`·`SAVEPOINT`·`RELEASE`는 DB 왕복이라
 * 트랜잭션 경계와 BEGIN→COMMIT 시간을 보여 주는 값은 있지만, 문제의 원인이었던 적은 없다.
 * 상세 목록에서는 접고, 느린 SQL 통계에서는 뺀다. 판정을 콘솔(상세)과 서버(통계)가 같이 쓰므로
 * 여기 한 곳에 둔다 — 한쪽만 고치면 "상세에서는 8건인데 통계는 다른 수"가 된다.
 *
 * 세션 설정(`SET`·`RESET`·`SHOW`·`SELECT set_config(`·`SELECT current_setting(`)도 같은 처지다.
 * OneWorks는 트랜잭션마다 `SELECT set_config('app.tenant_id', $1, true)`를 던지므로(멀티테넌트 RLS)
 * 이 문장이 느린 SQL 표의 맨 위를 차지한다. 값은 항상 같고 문제의 원인이었던 적이 없다.
 * 접는 쪽과 통계에서 빼는 쪽이 같은 판정을 써야 "상세는 k건인데 통계는 다른 수"가 되지 않는다.
 *
 * 선행 주석(블록 주석과 줄 주석)은 건너뛴다. OneWorks 쿼리는 주석으로 시작하는 것이 많고,
 * DB 감시가 붙이는 traceparent 주석은 문장 끝에 온다.
 */

import { scrubSql } from './scrub';

export const TX_CONTROL_VERBS = [
  'START TRANSACTION',
  'BEGIN',
  'COMMIT',
  'ROLLBACK',
  'SAVEPOINT',
  'RELEASE',
] as const;

/** 앞의 블록 주석과 줄 주석을 걷어낸다. 닫히지 않은 주석이면 undefined */
export function stripLeadingSqlComments(query: string): string | undefined {
  let rest = query;
  for (;;) {
    const trimmed = rest.trimStart();
    if (trimmed.startsWith('/*')) {
      const end = trimmed.indexOf('*/');
      if (end === -1) return undefined;
      rest = trimmed.slice(end + 2);
      continue;
    }
    if (trimmed.startsWith('--')) {
      const end = trimmed.indexOf('\n');
      if (end === -1) return undefined;
      rest = trimmed.slice(end + 1);
      continue;
    }
    return trimmed;
  }
}

/** 문장의 첫 낱말(대문자). `(`로 시작하는 서브쿼리도 건너뛴다. 없으면 undefined */
export function sqlVerb(query: string | undefined): string | undefined {
  if (!query) return undefined;
  const rest = stripLeadingSqlComments(query);
  if (rest === undefined) return undefined;
  const word = /^[(\s]*([A-Za-z]+)/.exec(rest);
  return word?.[1] ? word[1].toUpperCase() : undefined;
}

/**
 * 세션 설정 문장의 첫 낱말. `SET`은 `SET LOCAL`·`SET SESSION`까지 함께 잡힌다.
 * `UPDATE t SET x = 1`은 첫 낱말이 `UPDATE`라 걸리지 않는다
 */
export const SESSION_VERBS = ['SET', 'RESET', 'SHOW'] as const;

/** `SELECT`로 시작하지만 세션 설정인 함수들. OneWorks의 RLS 문장이 이 모양이다 */
export const SESSION_FUNCTIONS = ['set_config', 'current_setting'] as const;

const TX_CONTROL_PATTERN = new RegExp(
  `^[(\\s]*(?:${TX_CONTROL_VERBS.map((verb) => verb.replace(' ', '\\s+')).join('|')})\\b`,
  'i',
);

/** 트랜잭션 제어 문장인가. 대소문자 무시, 앞 주석 무시 */
export function isTxControlSql(query: string | undefined): boolean {
  if (!query) return false;
  const rest = stripLeadingSqlComments(query);
  return rest !== undefined && TX_CONTROL_PATTERN.test(rest);
}

const SESSION_BODY =
  `(?:${SESSION_VERBS.join('|')})\\b` + `|SELECT\\s+(?:${SESSION_FUNCTIONS.join('|')})\\s*\\(`;

const SESSION_PATTERN = new RegExp(`^[(\\s]*(?:${SESSION_BODY})`, 'i');

/** 세션 설정 문장인가. 대소문자 무시, 앞 주석 무시 (23-A회차) */
export function isSessionSql(query: string | undefined): boolean {
  if (!query) return false;
  const rest = stripLeadingSqlComments(query);
  return rest !== undefined && SESSION_PATTERN.test(rest);
}

/** 앞 주석을 건너뛰는 DuckDB 정규식 조각. 두 술어가 같이 쓴다 */
const DUCKDB_COMMENTS = '(?:\\s*(?:/\\*[\\s\\S]*?\\*/|--[^\\n]*\\n))*';

/**
 * 같은 판정을 DuckDB 조건으로. `isTxControlSql`과 한 몸이라 여기 둔다.
 * RE2 문법이라 `[\s\S]`·`*?`·`\b`가 다 된다. 컬럼 이름은 부르는 쪽이 준다
 */
export function txControlSql(column: string): string {
  const verbs = TX_CONTROL_VERBS.map((verb) => verb.replace(' ', '\\s+')).join('|');
  return `regexp_matches("${column}", '^${DUCKDB_COMMENTS}[(\\s]*(?:${verbs})\\b', 'i')`;
}

/** 세션 설정 판정의 DuckDB 짝. `isSessionSql`과 한 몸이다 (23-A회차) */
export function sessionSql(column: string): string {
  return `regexp_matches("${column}", '^${DUCKDB_COMMENTS}[(\\s]*(?:${SESSION_BODY})', 'i')`;
}

/**
 * JDBC 저장 프로시저 호출에서 프로시저 이름 (29회차).
 *
 * MES(`C:\Src\mes`)는 mapper 44개에 `{call PRD1003_R01(...)}`이 86건이다. 29회차는 OTel Java의
 * SQL 정규화기가 JDBC escape(`{`)에 막혀 **span 이름이 DB 이름 하나로 뭉친다**는 전제로 이것을
 * 넣었다.
 *
 * **그 전제는 사실이 아니다** (2026-09-10 실증, `02-결과/2026-09-10/29회차-MES실증-claude.md`
 * 4-2절). agent jar 안의 `SqlQueryAnalyzer`를 오프라인으로 돌려 보니 2.31.1은
 * `{call PRD1003_R01(...)}`에서 `operation=call`·`procedure=PRD1003_R01`을 제대로 뽑는다.
 * 그러니 이 함수는 **agent가 못 뽑는 경우의 안전망**이다 — 옛 판, 다른 언어의 계측, 문장만
 * 오고 이름은 뭉쳐 오는 경우. 걸릴 일이 없으면 아무 일도 하지 않는다: 이름을 못 뽑으면 지금
 * 동작 그대로이고, 뽑아도 span 이름이 이미 그 이름을 품고 있으면 손대지 않는다
 * (`normalizer.ts`의 `displayNameOf`). 실물 JDBC span 이름은 DB가 닿을 때 다시 본다
 * (재현 DB 미연결로 실증 항목 e는 미확인).
 *
 * 받는 모양:
 *   `{call NAME(...)}` · `{? = call NAME(...)}` · `call NAME(...)` · `CALL schema.NAME(...)`
 * 이름에 붙은 인용 부호(`` ` ``·`"`·`[]`)는 뗀다. 이름을 못 읽으면 undefined다.
 */
const PROCEDURE_CALL_PATTERN = /^\{?\s*(?:\?\s*=\s*)?call\s+([\w$."`[\]]+)/i;
const PROCEDURE_NAME_PATTERN = /^[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*$/;

export function procedureCallOf(query: string | undefined): string | undefined {
  if (!query) return undefined;
  const rest = stripLeadingSqlComments(query);
  if (rest === undefined) return undefined;
  const matched = PROCEDURE_CALL_PATTERN.exec(rest);
  if (!matched?.[1]) return undefined;
  const name = matched[1].replace(/[`"[\]]/g, '');
  return PROCEDURE_NAME_PATTERN.test(name) ? name : undefined;
}

/**
 * 느린 SQL을 묶는 열쇠 (29회차).
 *
 * 기본은 `scrubSql` 하나다 (24회차). 리터럴만 다른 문장을 한 줄로 접기 위한 것이다.
 * 저장 프로시저 호출은 거기에 더해 **이름으로** 접는다 — 같은 프로시저를 인자 수를 달리해
 * 부르는 경우(`CALLTYPE` 워크코드 분기)가 서로 다른 줄이 되면 "무엇이 DB 시간을 가장 많이
 * 먹는가"가 다시 흩어진다. SQL 식별자는 대소문자를 안 가리므로 열쇠에서는 대문자로 맞춘다.
 *
 * 서버 조회(DuckDB 스칼라 함수)와 다른 쓰임이 같은 함수를 봐야 판정이 갈라지지 않는다.
 */
export function sqlGroupKey(query: string): string {
  const procedure = procedureCallOf(query);
  return procedure === undefined ? scrubSql(query) : `CALL ${procedure.toUpperCase()}`;
}
